import React, { useState, useEffect, useRef } from 'react';
import {
  Container,
  Typography,
  Box,
  Button,
  IconButton,
  CircularProgress,
  Snackbar,
  TextField,
  Alert,
  styled,
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio
} from '@mui/material';
import apiService from '../../services/apiService';
import Draggable from 'react-draggable';
import CloseIcon from '@mui/icons-material/Close';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import { useNavigate } from 'react-router-dom';

const VisuallyHiddenInput = styled('input')({
  clip: 'rect(0 0 0 0)',
  clipPath: 'inset(50%)',
  height: 1,
  overflow: 'hidden',
  position: 'absolute',
  bottom: 0,
  left: 0,
  whiteSpace: 'nowrap',
  width: 1,
});

const DataExtraction = () => {
  const [file, setFile] = useState(null);
  const [fileId, setFileId] = useState('');
  const [suggestedColumns, setSuggestedColumns] = useState([]);
  const [personColumns, setPersonColumns] = useState([]);
  const [columnMapping, setColumnMapping] = useState({});
  const [extractedData, setExtractedData] = useState(null);
  const [errorData, setErrorData] = useState(null);
  const [confirmedMappings, setConfirmedMappings] = useState([]);
  const [draggingItem, setDraggingItem] = useState(null);
  const [headerRowIndex, setHeaderRowIndex] = useState(null);
  const [loading, setLoading] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('success');
  const [batchName, setBatchName] = useState('');
  const [pdfType, setPdfType] = useState('1'); // Default to type 1
  const [requiresBpsCheck, setRequiresBpsCheck] = useState(false);
  const [requiresApolloCheck, setRequiresApolloCheck] = useState(false);
  const navigate = useNavigate();
  const draggableRefs = useRef({});
  const initialPositions = useRef({});
  const [hoveredColumn, setHoveredColumn] = useState(null);

  useEffect(() => {
    const fetchPersonColumns = async () => {
      try {
        console.log('Fetching person columns...');
        const response = await apiService.get('/api/DataExtraction/obtenerColumnasDePersona');
        console.log('Person columns API response:', response);
        const mappedColumns = response || [];
        console.log('Mapped columns to set in state:', mappedColumns);
        setPersonColumns(mappedColumns);
        console.log('Person columns state set:', mappedColumns);
      } catch (error) {
        console.error('Error al obtener las columnas de persona:', error);
        showSnackbar('Error al obtener las columnas de persona', 'error');
      }
    };

    fetchPersonColumns();
  }, []);

  const showSnackbar = (message, severity = 'success') => {
    setSnackbarMessage(message);
    setSnackbarSeverity(severity);
    setSnackbarOpen(true);
  };

  const handleFileChange = async (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      // Validate file type
      const fileType = selectedFile.type;
      const fileExtension = selectedFile.name.split('.').pop().toLowerCase();
      
      if (fileType !== 'application/pdf' && 
          fileType !== 'application/vnd.ms-excel' && 
          fileType !== 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' &&
          !['pdf', 'xls', 'xlsx'].includes(fileExtension)) {
        showSnackbar('Por favor selecciona un archivo PDF o Excel (.pdf, .xls, .xlsx)', 'error');
        return;
      }

      setFile(selectedFile);
      // Set batch name from file name without extension
      const fileNameWithoutExtension = selectedFile.name.replace(/\.[^/.]+$/, "");
      setBatchName(fileNameWithoutExtension);
      
      const formData = new FormData();
      formData.append('file', selectedFile);
      formData.append('pdfType', pdfType.toString()); // Convert to string to ensure proper serialization

      try {
        setLoading(true);
        let endpoint = '';
        
        if (fileExtension === 'pdf') {
          endpoint = '/api/DataExtraction/uploadAndAnalyzePdf';
        } else {
          endpoint = '/api/DataExtraction/uploadAndAnalyzeExcel';
        }

        console.log(`Enviando archivo a ${endpoint} con pdfType=${pdfType}`);
        
        const response = await apiService.post(endpoint, formData, {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        });
        
        console.log('Response from server:', response);
        
        // Verificación más robusta de la respuesta
        if (!response) {
          throw new Error('No se recibió una respuesta del servidor');
        }
        
        // Asegurarse de que tenemos datos en la respuesta
        const responseData = response || response;
        
        if (!responseData) {
          throw new Error('La respuesta del servidor no contiene datos');
        }
        
        // Si hay un mensaje de error en la respuesta, mostrarlo
        if (responseData.message && responseData.message.includes("error")) {
          showSnackbar(responseData.message, 'error');
          setFile(null);
          return;
        }
        
        // Establecer los valores de estado con comprobaciones de seguridad
        setFileId(responseData.fileId || responseData.FileId || '');
        setSuggestedColumns(responseData.suggestedColumns || responseData.SuggestedColumns || []);
        setHeaderRowIndex(responseData.headerRowIndex !== undefined ? responseData.headerRowIndex : 
                         (responseData.HeaderRowIndex !== undefined ? responseData.HeaderRowIndex : 0));
        setRequiresBpsCheck(responseData.requiresBpsCheck || responseData.RequiresBpsCheck || false);
        setRequiresApolloCheck(responseData.requiresApolloCheck || responseData.RequiresApolloCheck || false);

        // Debugging para entender por qué no se muestra la UI de mapeo
        console.log('Estado después de procesar la respuesta:');
        console.log('fileId:', responseData.fileId || responseData.FileId);
        console.log('suggestedColumns:', responseData.suggestedColumns || responseData.SuggestedColumns);
        console.log('personColumns:', personColumns);
        console.log('Condición de renderizado:', 
          (responseData.suggestedColumns?.length > 0 || responseData.SuggestedColumns?.length > 0) && 
          personColumns.length > 0);

        // Verificar si tenemos columnas sugeridas
        const hasSuggestedColumns = 
          (responseData.suggestedColumns && responseData.suggestedColumns.length > 0) ||
          (responseData.SuggestedColumns && responseData.SuggestedColumns.length > 0);

        if (!hasSuggestedColumns) {
          showSnackbar('No se encontraron columnas sugeridas en el archivo', 'warning');
        } else {
          showSnackbar('Archivo cargado correctamente', 'success');
        }
      } catch (error) {
        console.error('Error al subir el archivo:', error);
        
        // Log detallado para depuración
        console.log('Error detallado:', {
          message: error.message,
          response: error.response,
          responseData: error.response?.data,
          responseHeaders: error.response?.headers,
          status: error.response?.status,
          stack: error.stack
        });
        
        // Extraer mensaje de error de la respuesta con mejor manejo de errores
        let errorMessage = 'Error al subir el archivo';
        
        if (error.response) {
          // El servidor respondió con un código de estado fuera del rango 2xx
          if (error.response.data) {
            if (typeof error.response.data === 'string') {
              errorMessage = error.response.data;
            } else if (error.response.data.message) {
              errorMessage = error.response.data.message;
            }
          } else {
            errorMessage = `Error del servidor: ${error.response.status}`;
          }
        } else if (error.request) {
          // La solicitud se realizó pero no se recibió respuesta
          errorMessage = 'No se recibió respuesta del servidor. Verifique su conexión a internet.';
        } else {
          // Algo ocurrió al configurar la solicitud
          errorMessage = error.message || errorMessage;
        }
        
        showSnackbar(errorMessage, 'error');
        
        // Restablecer el estado relacionado con el archivo
        setFile(null);
        setFileId('');
        setSuggestedColumns([]);
        setHeaderRowIndex(0); // Establecer un valor predeterminado seguro
      } finally {
        setLoading(false);
      }
    }
  };

  const handleProcessFile = async () => {
    if (Object.keys(columnMapping).length === 0) {
      showSnackbar('Por favor, mapea al menos una columna', 'warning');
      return;
    }

    if (!batchName.trim()) {
      showSnackbar('Por favor, ingresa un nombre para el batch', 'warning');
      return;
    }

    // Verificar si la columna Cédula está mapeada
    const hasCedulaMapping = Object.values(columnMapping).some(value => 
      value.toLowerCase() === 'cedula' || 
      value.toLowerCase() === 'cédula'
    );

    if (!hasCedulaMapping) {
      showSnackbar('Debe mapear la columna Cédula para poder procesar el archivo', 'error');
      return;
    }

    setLoading(true);

    const fileExtension = '.' + file.name.split('.').pop().toLowerCase();
    const requestData = {
      fileId: fileId,
      columnMapping: columnMapping,
      headerRowIndex: headerRowIndex,
      batchName: batchName,
      pdfType: pdfType,
      fileExtension: fileExtension,
      requiresBpsCheck: requiresBpsCheck,
      requiresApolloCheck: requiresApolloCheck
    };

    try {
      const response = await apiService.post('/api/DataExtraction/processFile', requestData);
      
      // Handle the response data regardless of status code
      const responseData = response;
      
      setExtractedData(responseData.validData || []);
      setErrorData(responseData.errorData || []);
      
      const totalValid = responseData.totalValid || 0;
      const totalErrors = responseData.totalErrors || 0;
      const totalSaved = responseData.totalSaved || 0;
      
      // Use the message from the server if available
      let message = responseData.message || `Procesamiento completado: ${totalValid} registros válidos`;
      let severity = 'success';
      
      // Determine severity based on the response
      if (response.status === 207) { // Partial success
        severity = 'warning';
      } else if (response.status === 422) { // Only errors
        severity = 'error';
      } else if (totalErrors > 0) { // Some errors with 200 status
        severity = totalValid > 0 ? 'warning' : 'error';
      }
      
      showSnackbar(message, severity);
    } catch (error) {
      console.error('Error al procesar el archivo:', error);
      
      // Check if we have a response with data that might indicate partial success
      if (error.response) {
        const responseData = error.response;
        
        if (error.response.status === 207) {
          // Partial success - 207 Multi-Status
          setExtractedData(responseData.validData || []);
          setErrorData(responseData.errorData || []);
          showSnackbar(responseData.message || 'Procesamiento parcial completado con algunos errores', 'warning');
        } else if (error.response.status === 422) {
          // Unprocessable Entity - 422
          setErrorData(responseData.errorData || []);
          showSnackbar(responseData.message || 'No se pudo procesar ningún registro', 'error');
        } else {
          // Other error status codes
          const errorMessage = responseData.message || 
                              responseData.title || 
                              (typeof responseData === 'string' ? responseData : 'Error al procesar el archivo');
          showSnackbar(errorMessage, 'error');
        }
      } else {
        // Network error or other client-side error
        showSnackbar('Error de conexión al procesar el archivo', 'error');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleDragStop = (e, data, suggestedColumn) => {
    console.log('Drag stop data:', data);
    const x = e.clientX;
    const y = e.clientY;
    console.log(`Mouse coordinates at drop: x=${x}, y=${y}`);
    const target = document.elementFromPoint(x, y);
    console.log('Drop target:', target);

    if (target && target.dataset && target.dataset.personColumn) {
      const personColumn = target.dataset.personColumn;
      if (!confirmedMappings.some(mapping => mapping[suggestedColumn] === personColumn)) {
        const newMapping = {
          [suggestedColumn]: personColumn,
        };
        setColumnMapping((prevMapping) => ({
          ...prevMapping,
          ...newMapping,
        }));
        setConfirmedMappings((prevMappings) => [...prevMappings, newMapping]);
        showSnackbar('Columna mapeada exitosamente', 'success');

        const targetRect = target.getBoundingClientRect();
        const draggableRef = draggableRefs.current[suggestedColumn];
        if (draggableRef && draggableRef.current) {
          draggableRef.current.style.position = 'absolute';
          draggableRef.current.style.left = `${targetRect.right + 10}px`;
          draggableRef.current.style.top = `${targetRect.top}px`;
        }
      } 
    }
    setHoveredColumn(null);
  };

  const handleRemoveMapping = (index) => {
    const mappingToRemove = confirmedMappings[index];
    const updatedMappings = confirmedMappings.filter((_, i) => i !== index);
    const updatedColumnMapping = { ...columnMapping };
    delete updatedColumnMapping[Object.keys(mappingToRemove)[0]];

    setConfirmedMappings(updatedMappings);
    setColumnMapping(updatedColumnMapping);

    const suggestedColumn = Object.keys(mappingToRemove)[0];
    const draggableRef = draggableRefs.current[suggestedColumn];
    if (draggableRef && draggableRef.current) {
      const initialPosition = initialPositions.current[suggestedColumn];
      if (initialPosition) {
        draggableRef.current.style.left = `${initialPosition.x}px`;
        draggableRef.current.style.top = `${initialPosition.y}px`;
      }
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbarOpen(false);
  };

  // Add utility function for date formatting
  const formatDate = (dateString) => {
    if (!dateString) return '-';
    
    try {
      // Handle ISO date format (2023-01-01T00:00:00)
      const date = new Date(dateString);
      
      // Check if date is valid
      if (isNaN(date.getTime())) {
        console.warn(`Invalid date format: ${dateString}`);
        return '-';
      }
      
      // Format as DD/MM/YYYY
      return date.toLocaleDateString('es-UY', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric'
      });
    } catch (error) {
      console.error(`Error formatting date: ${dateString}`, error);
      return '-';
    }
  };

  return (
    <Container>
      <Box my={4}>
        <Typography variant="h4">Extracción de Datos</Typography>
      </Box>

      <Box mb={4}>
        <FormControl component="fieldset">
          <FormLabel component="legend">Tipo de Archivo</FormLabel>
          <RadioGroup
            row
            value={pdfType}
            onChange={(e) => setPdfType(e.target.value)}
          >
            <FormControlLabel 
              value="1" 
              control={<Radio />} 
              label={
                <Box>
                  <Typography variant="subtitle1">Solo Cédula</Typography>
                  {pdfType === "1" && (
                    <Typography variant="caption" color="textSecondary" sx={{ fontSize: '0.7rem' }}>
                      Columnas requeridas: Cédula
                    </Typography>
                  )}
                </Box>
              }
            />
            <FormControlLabel 
              value="2" 
              control={<Radio />} 
              label={
                <Box>
                  <Typography variant="subtitle1">Cédula, Nombre y Apellido</Typography>
                  {pdfType === "2" && (
                    <Typography variant="caption" color="textSecondary" sx={{ fontSize: '0.7rem' }}>
                      Columnas requeridas: Cédula, Nombre/Apellido
                    </Typography>
                  )}
                </Box>
              }
            />
            <FormControlLabel 
              value="3" 
              control={<Radio />} 
              label={
                <Box>
                  <Typography variant="subtitle1">Afiliados AFAP (Apollo)</Typography>
                  {pdfType === "3" && (
                    <Typography variant="caption" color="textSecondary" sx={{ fontSize: '0.7rem' }}>
                      Columnas requeridas: Cédula, Nombre/Apellido, Estado de afiliación
                    </Typography>
                  )}
                </Box>
              }
            />
          </RadioGroup>
        </FormControl>
      </Box>

      {!fileId && (
        <Box 
          display="flex" 
          flexDirection="column" 
          alignItems="center" 
          p={3} 
          border="2px dashed #ccc"
          borderRadius={2}
          bgcolor="#fafafa"
          mb={4}
        >
          <Button
            component="label"
            variant="contained"
            startIcon={<CloudUploadIcon />}
            sx={{ mb: 2 }}
            disabled={loading}
          >
            {loading ? 'Procesando...' : 'Seleccionar Archivo'}
            <VisuallyHiddenInput 
              type="file"
              accept=".pdf,.xls,.xlsx,application/pdf,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
              onChange={handleFileChange}
            />
          </Button>
          <Typography variant="body2" color="textSecondary">
            Arrastra y suelta un archivo PDF o Excel, o haz clic para seleccionarlo
          </Typography>
          {loading && (
            <Box mt={2}>
              <CircularProgress size={24} />
            </Box>
          )}
        </Box>
      )}

      {(suggestedColumns.length > 0 && personColumns.length > 0) && (
        <Box my={4}>
          <Typography variant="h6">Mapea las columnas del archivo:</Typography>
          <Box display="flex" justifyContent="space-between">
            <Box>
              <Typography variant="subtitle1">Columnas del archivo</Typography>
              {suggestedColumns.map((suggestedColumn) => (
                <Draggable
                  key={suggestedColumn}
                  onStart={() => {
                    setDraggingItem(suggestedColumn);
                    if (!initialPositions.current[suggestedColumn]) {
                      const draggableRef = draggableRefs.current[suggestedColumn];
                      if (draggableRef && draggableRef.current) {
                        const rect = draggableRef.current.getBoundingClientRect();
                        initialPositions.current[suggestedColumn] = { x: rect.left, y: rect.top };
                      }
                    }
                  }}
                  onStop={(e, data) => {
                    handleDragStop(e, data, suggestedColumn);
                    setDraggingItem(null);
                  }}
                  onDrag={(e, data) => {
                    const x = e.clientX;
                    const y = e.clientY;
                    const target = document.elementFromPoint(x, y);
                    if (target && target.dataset && target.dataset.personColumn) {
                      setHoveredColumn(target.dataset.personColumn);
                    } else {
                      setHoveredColumn(null);
                    }
                  }}
                  nodeRef={
                    (draggableRefs.current[suggestedColumn] =
                      draggableRefs.current[suggestedColumn] || React.createRef())
                  }
                >
                  <Box
                    ref={draggableRefs.current[suggestedColumn]}
                    p={1}
                    m={1}
                    bgcolor="lightblue"
                    borderRadius="4px"
                    style={{
                      cursor: 'pointer',
                      pointerEvents: draggingItem === suggestedColumn ? 'none' : 'auto',
                    }}
                  >
                    {suggestedColumn}
                  </Box>
                </Draggable>
              ))}
            </Box>
            <Box>
              <Typography variant="subtitle1">Columnas de nuestra Base de Datos</Typography>
              {personColumns.map((personColumn) => {
                const isMapped = confirmedMappings.some(mapping => Object.values(mapping)[0] === personColumn);
                return (
                  <Box
                    key={personColumn}
                    p={1}
                    m={1}
                    bgcolor={isMapped ? 'lightgreen' : hoveredColumn === personColumn ? 'lightyellow' : 'lightgray'}
                    borderRadius="4px"
                    data-person-column={personColumn}
                  >
                    {personColumn}
                  </Box>
                );
              })}
            </Box>
          </Box>
          <Box mt={4} mb={2}>
            <TextField
              label="Nombre del Batch"
              variant="outlined"
              fullWidth
              value={batchName}
              onChange={(e) => setBatchName(e.target.value)}
            />
          </Box>
          <Box mt={2}>
            <Button 
              variant="contained" 
              color="primary" 
              onClick={handleProcessFile} 
              disabled={loading || confirmedMappings.length === 0 || !batchName.trim()}
            >
              {loading ? <CircularProgress size={24} /> : 'Procesar Archivo'}
            </Button>
          </Box>
          {confirmedMappings.length > 0 && (
            <Box mt={2} p={2} border="1px solid #ccc" borderRadius="4px" bgcolor="#f9f9f9">
              <Typography variant="h6">Columnas Mapeadas:</Typography>
              {confirmedMappings.map((mapping, index) => (
                <Box key={index} display="flex" alignItems="center" my={1}>
                  <Typography>
                    {Object.keys(mapping)[0]} - {Object.values(mapping)[0]}
                  </Typography>
                  <IconButton onClick={() => handleRemoveMapping(index)} size="small">
                    <CloseIcon />
                  </IconButton>
                </Box>
              ))}
            </Box>
          )}
        </Box>
      )}

      {extractedData && extractedData.length > 0 && (
        <Box my={4}>
          <Typography variant="h6" color="primary">
            Datos Extraídos Correctamente ({extractedData.length}):
          </Typography>
          <Box sx={{ maxHeight: '400px', overflow: 'auto', border: '1px solid #ccffcc', borderRadius: '4px', p: 2, bgcolor: '#f8fff8' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr>
                  {/* Siempre mostrar la columna de Cédula si está mapeada */}
                  {Object.values(columnMapping).some(value => value.toLowerCase() === 'cedula') && (
                    <th style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'left' }}>Cédula</th>
                  )}
                  {/* Mostrar Nombre solo si está mapeado */}
                  {Object.values(columnMapping).some(value => value.toLowerCase() === 'nombre') && (
                    <th style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'left' }}>Nombre</th>
                  )}
                  {/* Mostrar Apellido solo si está mapeado */}
                  {Object.values(columnMapping).some(value => value.toLowerCase() === 'apellido') && (
                    <th style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'left' }}>Apellido</th>
                  )}
                  {/* Mostrar Fecha de Nacimiento solo si está mapeada */}
                  {Object.values(columnMapping).some(value => value.toLowerCase() === 'fechadenacimiento') && (
                    <th style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'left' }}>Fecha de Nacimiento</th>
                  )}
                  {/* Mostrar Estado de Afiliación solo si está mapeado y es tipo 3 */}
                  {pdfType === '3' && Object.values(columnMapping).some(value => 
                    value.toLowerCase() === 'mensajebps' || value.toLowerCase() === 'estado de afiliación') && (
                    <th style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'left' }}>Estado de Afiliación</th>
                  )}
                  {/* Mostrar otras columnas mapeadas */}
                  {Object.values(columnMapping)
                    .filter(value => {
                      const lowerValue = value.toLowerCase();
                      return !['cedula', 'nombre', 'apellido', 'fecha de nacimiento', 'fechadenacimiento', 'mensajebps', 'estado de afiliación'].includes(lowerValue);
                    })
                    .map((mappedColumn, index) => (
                      <th key={index} style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'left' }}>
                        {mappedColumn.charAt(0).toUpperCase() + mappedColumn.slice(1)}
                      </th>
                    ))
                  }
                </tr>
              </thead>
              <tbody>
                {extractedData.map((row, index) => (
                  <tr key={index} style={{ backgroundColor: index % 2 === 0 ? '#fff' : '#f9f9f9' }}>
                    {/* Mostrar Cédula solo si está mapeada */}
                    {Object.values(columnMapping).some(value => value.toLowerCase() === 'cedula') && (
                      <td style={{ border: '1px solid #ddd', padding: '8px' }}>{row.cedula}</td>
                    )}
                    {/* Mostrar Nombre solo si está mapeado */}
                    {Object.values(columnMapping).some(value => value.toLowerCase() === 'nombre') && (
                      <td style={{ border: '1px solid #ddd', padding: '8px' }}>{row.nombre || '-'}</td>
                    )}
                    {/* Mostrar Apellido solo si está mapeado */}
                    {Object.values(columnMapping).some(value => value.toLowerCase() === 'apellido') && (
                      <td style={{ border: '1px solid #ddd', padding: '8px' }}>{row.apellido || '-'}</td>
                    )}
                    {/* Mostrar Fecha de Nacimiento solo si está mapeada */}
                    {Object.values(columnMapping).some(value => value.toLowerCase() === 'fechadenacimiento') && (
                      <td style={{ border: '1px solid #ddd', padding: '8px' }}>{formatDate(row.fechaDeNacimiento)}</td>
                    )}
                    {/* Mostrar Estado de Afiliación solo si está mapeado y es tipo 3 */}
                    {pdfType === '3' && Object.values(columnMapping).some(value => 
                      value.toLowerCase() === 'mensajebps' || value.toLowerCase() === 'estado de afiliación') && (
                      <td style={{ border: '1px solid #ddd', padding: '8px' }}>{row.mensajeBPS || '-'}</td>
                    )}
                    {/* Mostrar otras columnas mapeadas */}
                    {Object.values(columnMapping)
                      .filter(value => {
                        const lowerValue = value.toLowerCase();
                        return !['cedula', 'nombre', 'apellido', 'fecha de nacimiento', 'fechadenacimiento', 'mensajebps', 'estado de afiliación'].includes(lowerValue);
                      })
                      .map((mappedColumn, colIndex) => {
                        // Convertir el nombre de la columna a camelCase para acceder a la propiedad
                        const propName = mappedColumn.toLowerCase();
                        const camelCaseProp = propName.charAt(0).toLowerCase() + 
                          propName.slice(1).replace(/\s(.)/g, function($1) { return $1.toUpperCase(); })
                          .replace(/\s/g, '');
                        
                        return (
                          <td key={colIndex} style={{ border: '1px solid #ddd', padding: '8px' }}>
                            {camelCaseProp === 'fechaDeNacimiento' 
                              ? formatDate(row[camelCaseProp]) 
                              : (row[camelCaseProp] !== undefined ? row[camelCaseProp] : '-')}
                          </td>
                        );
                      })
                    }
                  </tr>
                ))}
              </tbody>
            </table>
          </Box>
        </Box>
      )}

      {errorData && errorData.length > 0 && (
        <Box my={4}>
          <Typography variant="h6" color="error">
            Filas con Errores ({errorData.length}):
          </Typography>
          <Box sx={{ maxHeight: '400px', overflow: 'auto', border: '1px solid #ffcccc', borderRadius: '4px', p: 2, bgcolor: '#fff8f8' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr>
                  <th style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'left' }}>Fila</th>
                  {/* Mostrar Cédula solo si está mapeada */}
                  {Object.values(columnMapping).some(value => value.toLowerCase() === 'cedula') && (
                    <th style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'left' }}>Cédula</th>
                  )}
                  {/* Mostrar Nombre solo si está mapeado */}
                  {Object.values(columnMapping).some(value => value.toLowerCase() === 'nombre') && (
                    <th style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'left' }}>Nombre</th>
                  )}
                  {/* Mostrar Apellido solo si está mapeado */}
                  {Object.values(columnMapping).some(value => value.toLowerCase() === 'apellido') && (
                    <th style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'left' }}>Apellido</th>
                  )}
                  {/* Mostrar otras columnas mapeadas */}
                  {Object.values(columnMapping)
                    .filter(value => {
                      const lowerValue = value.toLowerCase();
                      return !['cedula', 'nombre', 'apellido', 'fecha de nacimiento', 'fechadenacimiento', 'mensajebps', 'estado de afiliación'].includes(lowerValue);
                    })
                    .map((mappedColumn, index) => (
                      <th key={index} style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'left' }}>
                        {mappedColumn.charAt(0).toUpperCase() + mappedColumn.slice(1)}
                      </th>
                    ))
                  }
                  <th style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'left' }}>Error</th>
                </tr>
              </thead>
              <tbody>
                {errorData.map((row, index) => (
                  <tr key={index} style={{ backgroundColor: index % 2 === 0 ? '#fff' : '#f9f9f9' }}>
                    <td style={{ border: '1px solid #ddd', padding: '8px' }}>{row.rowIndex}</td>
                    {/* Mostrar Cédula solo si está mapeada */}
                    {Object.values(columnMapping).some(value => value.toLowerCase() === 'cedula') && (
                      <td style={{ border: '1px solid #ddd', padding: '8px' }}>{row.cedula || '-'}</td>
                    )}
                    {/* Mostrar Nombre solo si está mapeado */}
                    {Object.values(columnMapping).some(value => value.toLowerCase() === 'nombre') && (
                      <td style={{ border: '1px solid #ddd', padding: '8px' }}>{row.nombre || '-'}</td>
                    )}
                    {/* Mostrar Apellido solo si está mapeado */}
                    {Object.values(columnMapping).some(value => value.toLowerCase() === 'apellido') && (
                      <td style={{ border: '1px solid #ddd', padding: '8px' }}>{row.apellido || '-'}</td>
                    )}
                    {/* Mostrar otras columnas mapeadas */}
                    {Object.values(columnMapping)
                      .filter(value => {
                        const lowerValue = value.toLowerCase();
                        return !['cedula', 'nombre', 'apellido', 'fecha de nacimiento', 'fechadenacimiento', 'mensajebps', 'estado de afiliación'].includes(lowerValue);
                      })
                      .map((mappedColumn, colIndex) => {
                        // Convertir el nombre de la columna a camelCase para acceder a la propiedad
                        const propName = mappedColumn.toLowerCase();
                        const camelCaseProp = propName.charAt(0).toLowerCase() + 
                          propName.slice(1).replace(/\s(.)/g, function($1) { return $1.toUpperCase(); })
                          .replace(/\s/g, '');
                        
                        return (
                          <td key={colIndex} style={{ border: '1px solid #ddd', padding: '8px' }}>
                            {camelCaseProp === 'fechaDeNacimiento' 
                              ? formatDate(row[camelCaseProp]) 
                              : (row[camelCaseProp] !== undefined ? row[camelCaseProp] : '-')}
                          </td>
                        );
                      })
                    }
                    <td style={{ border: '1px solid #ddd', padding: '8px', color: 'red' }}>{row.errorMessage}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Box>
        </Box>
      )}

      <Snackbar 
        open={snackbarOpen} 
        autoHideDuration={3000} 
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbarSeverity} sx={{ width: '100%' }}>
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default DataExtraction;
